<!--
  @fileoverview Dropdown Selection Component

  Renders a dropdown select with optional multi-select support.
  Used for structured data collection in chat conversations.

  @component DropdownSelection
  @displayName Dropdown Select

  Features:
  - Single or multi-select modes
  - Filterable options
  - Optional saveAsBoolean mode for checkbox-like behavior
  - Submit button with configurable label
  - Collapse tags for multi-select display

  @example
  <DropdownSelection
    :selections="selectionsConfig"
    :button-outline="'primary'"
    :should-disable-if-in-livechat="false"
    @sendPostback="handleSelection"
  />

  @vue3migration
  - Converted to script setup pattern
  - Replaced this.$nextTick with nextTick import
  - Used template refs with ref() for element access
-->
<template>
  <div class="relative">
    <el-select
      v-model="selected"
      placeholder="Select"
      :multiple="allowMultiselect"
      :collapse-tags="allowMultiselect"
      :popper-append-to-body="false"
      filterable
      class="w-full my-[10px] mx-0"
      ref="dropdownQ"
      @change="onChange"
      popper-class="keyreply-dropdown"
    >
      <el-option
        v-for="(item, index) in selections.data"
        :key="`selections-${index}`"
        :label="item.key"
        :value="item.value"
        class="dropdown-option"
      >
      </el-option>
    </el-select>
    <div class="flex flex-col">
      <!-- @vue3migration: Removed size="small" - Element Plus default size is equivalent -->
      <el-button
        plain
        class="w-full mb-[10px] mt-0 mx-0 whitespace-normal dropdown-button"
        :type="buttonOutline"
        @click="submitButtonClicked"
      >
        {{ selections.label }}
      </el-button>
    </div>
  </div>
</template>

<script setup>
/**
 * @module components/ChatWindow/Message/DropdownSelection
 * @description Dropdown selection component for structured data input
 */
import { ref, computed, nextTick } from "vue";
import * as _ from "lodash-es";

/**
 * Component props
 */
const props = defineProps({
  selections: {
    type: Object,
    required: true,
  },
  buttonOutline: {
    type: String,
    default: "",
  },
});

/**
 * Component emits
 */
const emit = defineEmits(["sendPostback"]);

/**
 * Template ref for dropdown element
 */
const dropdownQ = ref(null);

/**
 * Currently selected value(s)
 */
const selected = ref(null);

/**
 * Check if multi-select mode is enabled
 */
const allowMultiselect = computed(() => {
  return props.selections.options?.multiselect || false;
});

/**
 * Check if saveAsBoolean mode is enabled
 *
 * When enabled, each selected option is saved as a separate boolean field
 * rather than a single comma-separated value.
 */
const saveAsBoolean = computed(() => {
  return props.selections.options?.saveAsBoolean || false;
});

/**
 * Check if current selection is multi-value
 */
const isSelectedValueMultiselect = computed(() => {
  return Array.isArray(selected.value) && selected.value.length;
});

/**
 * Get the selected value(s) as a string
 *
 * For multi-select, joins values with commas.
 */
const selectedValue = computed(() => {
  return isSelectedValueMultiselect.value
    ? mapKeepSorts(",", (o) => o.value)
    : selected.value;
});

/**
 * Get the selected label(s) as a string
 *
 * For multi-select, joins labels with commas.
 */
const selectedLabel = computed(() => {
  return isSelectedValueMultiselect.value
    ? mapKeepSorts(", ", (o) => o.key)
    : _.find(props.selections.data, { value: selected.value })?.key || selected.value;
});

/**
 * Handle submit button click
 *
 * Builds the postback payload based on selection mode:
 * - Normal mode: Sets key and key_label
 * - saveAsBoolean mode: Sets key_value for each option as true/false
 */
function submitButtonClicked() {
  if (selectedValue.value) {
    let payload = {};
    if (saveAsBoolean.value) {
      const all = props.selections.data.map((o) => o.value);
      const selecteds = selectedValue.value.split(",");
      const unselecteds = _.xor(all, selecteds);
      const dataToSet = selecteds.reduce(
        (acc, curr) => ({ ...acc, [`${props.selections.key}_${curr}`]: true }),
        {
          [`${props.selections.key}_label`]: selectedLabel.value,
        }
      );
      const dataToUnset = unselecteds.reduce(
        (acc, curr) => ({ ...acc, [`${props.selections.key}_${curr}`]: false }),
        {}
      );
      payload = {
        data: { ...dataToSet, ...dataToUnset },
      };
    } else {
      payload = {
        data: {
          [props.selections.key]: selectedValue.value,
          [`${props.selections.key}_label`]: selectedLabel.value,
        },
      };
    }
    _.set(payload, "next", props.selections.next);
    emit("sendPostback", {
      text: selectedLabel.value,
      event: "set",
      data: payload,
    });
  }
}

/**
 * Map and join selected values while preserving original order
 *
 * @param {string} delimiter - Join delimiter
 * @param {Function} cb - Callback to extract value from each item
 * @returns {string} Joined string
 */
function mapKeepSorts(delimiter, cb) {
  return _.filter(props.selections.data, (o) => selected.value.includes(o.value))
    .map(cb)
    .join(delimiter);
}

/**
 * Handle selection change for multi-select
 *
 * Truncates the displayed tag text for better UX.
 */
function onChange() {
  if (isSelectedValueMultiselect.value) {
    nextTick(() => {
      const firstSelectedValue = selected.value[0];
      const firstSelectedLabel = _.find(props.selections.data, {
        value: firstSelectedValue,
      })?.key;
      const tagsText = dropdownQ.value?.$el?.querySelector(".el-select__tags-text");
      if (tagsText && firstSelectedLabel) {
        tagsText.textContent = `${firstSelectedLabel.substr(0, 15)}...`;
      }
    });
  }
}
</script>

<style scoped>
.dropdown-option,
.dropdown-button {
  font-family: var(--keyreply-font-family);
}

.dropdown-option {
  color: #333;
}

.el-select-dropdown__item {
  max-width: 350px;
  white-space: normal;
  overflow: unset;
  height: auto;
  line-height: normal;
  padding-top: 10px;
  padding-bottom: 10px;
}

:deep(.el-select) {
  position: relative !important;
}

:deep(.el-scrollbar__view) {
  padding: 10px;
}

:deep(.el-select-dropdown__item) {
  max-width: 100%;
}
</style>

<style>
.keyreply-dropdown {
  position: absolute !important;
  top: 100% !important;
  left: 0 !important;
}
</style>
